<template>
  <div class="gl-balance-widget">
    <div v-if="loading" class="text-center py-6">
      <v-progress-circular indeterminate size="32" color="primary" />
    </div>
    <div v-else-if="fetchError" class="text-center py-4">
      <v-icon color="grey lighten-1" size="32">mdi-alert-circle-outline</v-icon>
      <p class="text-caption grey--text mt-2">Unable to load data</p>
    </div>
    <div v-else>
      <div
        class="kpi-summary kpi-icon-clickable"
        :class="isBalanced ? 'kpi-summary-ok' : 'kpi-summary-err'"
        @click="navigate(summaryRoute)"
      >
        <div class="kpi-icon" :class="isBalanced ? 'kpi-icon-ok' : 'kpi-icon-err'">
          <v-icon size="24" :color="toneColor(isBalanced ? 'green' : 'red')">
            {{ isBalanced ? icons.mdiShieldCheck : icons.mdiAlertCircle }}
          </v-icon>
        </div>
        <div class="kpi-text">
          <div class="kpi-number" :class="toneText(isBalanced ? 'green' : 'red')">
            {{ passedChecks }}<span class="kpi-denom">/{{ totalChecks }}</span>
          </div>
          <div class="text-caption grey--text">Checks Passed</div>
        </div>
        <v-icon size="16" color="grey lighten-1" class="ml-auto">
          mdi-chevron-right
        </v-icon>
      </div>

      <div class="balance-list mt-2">
        <div
          v-for="item in checks"
          :key="item.label"
          class="balance-row kpi-icon-clickable"
          :class="item.matchAmtFlag ? 'balance-row-ok' : 'balance-row-err'"
          @click="navigate(labelToRoute(item.label))"
        >
          <v-icon size="14" :color="toneColor(item.matchAmtFlag ? 'green' : 'red')">
            {{ item.matchAmtFlag ? icons.mdiShieldCheck : icons.mdiAlertCircle }}
          </v-icon>
          <span class="balance-label">{{ item.label }}</span>
          <span :class="item.matchAmtFlag ? 'balance-amount-ok' : 'balance-amount-err'">
            {{ diffLabel(item) }}
          </span>
          <v-icon size="14" color="grey lighten-1">mdi-chevron-right</v-icon>
        </div>
      </div>

      <div v-if="noPeriodData" class="text-caption grey--text text-center mt-3">
        No GL entries posted for this period yet
      </div>
      <div v-if="comparePeriod" class="text-caption grey--text text-center mt-3">
        {{ comparePeriod.from }} – {{ comparePeriod.to }}
      </div>
    </div>
  </div>
</template>

<script>
import {
  mdiShieldCheck,
  mdiAlertCircle,
} from '@mdi/js';
import { clients } from '../../../util/clients';
import WidgetThemeMixin from '../WidgetTheme.mixin';

const { backendRest } = clients.direct;

const LABEL_ROUTES = {
  'Current Tax': '/gl/utils/checkTaxPaymentBalToGL',
  'Prior Tax': '/gl/utils/checkTaxPaymentBalToGL',
  'Back Tax': '/gl/utils/checkTaxPaymentBalToGL',
  'Misc Receipts': '/gl/utils/checkMISCBalToGL',
  'MTG Tax': '/gl/utils/checkMTGBalToGL',
  'MTG Cert Fees': '/gl/utils/checkMTGBalToGL',
  'Unposted Payments': '/gl/utils',
};

export default {
  name: 'GlBalanceStatusWidget',
  mixins: [WidgetThemeMixin],
  data: () => ({
    icons: { mdiShieldCheck, mdiAlertCircle },
    loading: true,
    fetchError: false,
    isBalanced: true,
    totalChecks: 0,
    passedChecks: 0,
    checks: [],
    outOfBalance: [],
    comparePeriod: null,
    noPeriodData: false,
  }),
  computed: {
    summaryRoute() {
      if (this.outOfBalance.length > 0) {
        return this.labelToRoute(this.outOfBalance[0].label);
      }
      return '/gl/utils/checkTaxPaymentBalToGL';
    },
  },
  async created() {
    await this.fetchData();
  },
  methods: {
    formatAmount(val) {
      if (!val) return '$0';
      const abs = Math.abs(val);
      if (abs >= 1000000) return `$${(abs / 1000000).toFixed(1)}M`;
      if (abs >= 1000) return `$${(abs / 1000).toFixed(1)}K`;
      return `$${abs.toFixed(2)}`;
    },
    diffLabel(item) {
      if (item.matchAmtFlag) return 'Balanced';
      return item.isCount ? `${item.diff} unposted` : this.formatAmount(item.diff);
    },
    labelToRoute(label) {
      return LABEL_ROUTES[label] || '/gl/utils';
    },
    navigate(path) {
      window.open(path, '_blank');
    },
    async fetchData() {
      this.loading = true;
      try {
        const resp = await backendRest.get('/dashboard/gl-balance');
        const data = resp.data || {};
        this.isBalanced = data.isBalanced || false;
        this.totalChecks = data.totalChecks || 0;
        this.passedChecks = data.passedChecks || 0;
        this.checks = data.checks || [];
        this.outOfBalance = data.outOfBalance || [];
        this.comparePeriod = data.comparePeriod || null;
        this.noPeriodData = !!data.noPeriodData;
      } catch (e) {
        console.error('GL balance fetch error', e);
        this.fetchError = true;
        this.isBalanced = true;
        this.totalChecks = 0;
      } finally {
        this.loading = false;
      }
    },
    async refresh() {
      await this.fetchData();
    },
  },
};
</script>

<style scoped>
.kpi-summary {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid transparent;
  transition: background 0.15s, border-color 0.15s;
}
.kpi-summary-ok {
  background: var(--widget-panel-ok-bg, #f1f8f1);
  border-color: var(--widget-panel-ok-border, #c8e6c9);
}
.kpi-summary-err {
  background: var(--widget-panel-err-bg, #fff5f5);
  border-color: var(--widget-panel-err-border, #ffcdd2);
}
.kpi-summary.kpi-icon-clickable:hover {
  filter: brightness(0.96);
}
.kpi-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.kpi-icon-ok {
  background: var(--widget-tint-ok, linear-gradient(135deg, #e8f5e9, #c8e6c9));
}
.kpi-icon-err {
  background: var(--widget-tint-err, linear-gradient(135deg, #ffebee, #ffcdd2));
}
.kpi-text { flex: 1; line-height: 1.2; }
.kpi-number {
  font-size: 1.6rem;
  font-weight: 800;
  letter-spacing: -1px;
  line-height: 1;
}
.kpi-denom {
  font-size: 0.95rem;
  font-weight: 400;
  color: var(--widget-muted-text, #9e9e9e);
}
.balance-list {
  border-top: 1px solid var(--widget-hairline, #f0f0f0);
  padding-top: 6px;
}
.balance-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 4px;
  border-radius: 6px;
  transition: background 0.12s;
}
.balance-row-ok:hover {
  background: var(--widget-panel-ok-bg, #f1f8f1);
}
.balance-row-err:hover {
  background: var(--widget-panel-err-bg, #fff5f5);
}
.balance-row:hover {
  background: var(--widget-hover-bg, #f5f5f5);
}
.balance-amount-ok {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--widget-ok-text, #2e7d32);
}
.balance-amount-err {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--widget-err-text, #c62828);
}
.balance-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #e53935;
  flex-shrink: 0;
}
.balance-label {
  flex: 1;
  font-size: 0.8rem;
  color: var(--widget-body-text, #424242);
}
.balance-amount {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--widget-err-text, #c62828);
}
.kpi-icon-clickable { cursor: pointer; }
</style>
